<template>
  <ViewContainer
    :title="`${$t('common.rev_other')} in ${organization.name}`"
    :breadcrumb="breadcrumbs"
  >
    <v-card>
      <v-card-title>
        <v-row class="my-1">
          <v-col cols="5">
            <SearchInput
              class="mr-4"
              :value="searchTerms"
              @change="handleSearch"
            />
          </v-col>
          <v-col></v-col>
          <v-col cols="1">
            <OrganizationMenu :organizationId="organization._id" />
          </v-col>
        </v-row>
      </v-card-title>
      <v-data-table
        id="data-organization-revs"
        class="elevation-1"
        disable-sort
        data-private
        calculate-widths
        :headers="headers"
        :items="items"
        :options.sync="options"
        :footer-props="footerOptions"
        :server-items-length="totalRecordsInData"
        :loading="isLoading"
      >
        <template v-slot:item.name="{ item }">
          <router-link
            class="cursor-pointer text-decoration-none"
            :to="getRoute(item.type, item.id)"
          >
            {{ item.name }}
          </router-link>
        </template>
        <template v-slot:item.actions="{ item }">
          <v-icon small @click="openRevRemovalConfirmationModal(item)">
            mdi-delete-outline
          </v-icon>
        </template>
      </v-data-table>
    </v-card>
    <DeleteConfirmationModal
      :show="showDeleteModal"
      :isLoading="isDeleting"
      :title="$t('rev.delete_this_rev')"
      :text="$t('rev.are_you_sure_you_want_to_remove_permanently_this_rev')"
      @cancel="closeModal"
      @delete="removeRevByType"
    />
  </ViewContainer>
</template>

<script>
import mixin from '@/mixins/mixin.vue'
import trackerMixin from '@/mixins/tracking/trackerMixin.vue'

import OrganizationMenu from './OrganizationMenu.vue'
import ViewContainer from '@/components/Core/Layout/ViewContainer.vue'
import SearchInput from '@/components/Core/SearchInput.vue'
import DeleteConfirmationModal from '@/components/Modals/DeleteConfirmationModal.vue'

import _ from 'lodash'
import { mapActions, mapState } from 'vuex'
import { utils } from 'frontend-js-shared'

export default {
  name: 'OrganizationsRevs',

  mixins: [mixin, trackerMixin],

  components: {
    ViewContainer,
    OrganizationMenu,
    SearchInput,
    DeleteConfirmationModal,
  },

  data() {
    return {
      breadcrumbs: [],
      showDeleteModal: false,
      rev: {},
      organizationId: this.getRouteParams().organizationId,
      footerOptions: {
        itemsPerPageOptions: [20, 50, 100],
      },
      headers: [
        {
          text: _.capitalize(this.$t('common.name')),
          value: 'name',
          isSortable: false,
          align: 'start',
        },
        {
          text: _.capitalize(this.$t('common.types')),
          value: 'typeText',
          isSortable: false,
          align: 'start',
        },
        {
          text: _.capitalize(this.$t('common.roles.owner')),
          isSortable: false,
          value: 'owner',
          align: 'start',
        },
        {
          text: _.capitalize(this.$t('common.roles.coach_one')),
          isSortable: false,
          value: 'coach',
          align: 'start',
        },
        {
          text: _.capitalize(this.$t('common.state')),
          value: 'state',
          align: 'start',
          isSortable: false,
        },
        {
          value: 'condition',
          text: _.capitalize(this.$t('common.rev_condition.rev_condition')),
          isSortable: false,
          align: 'start',
        },
        this.$isFeatureEnabled(this.$featuresKeys?.points) && {
          text: _.capitalize(this.$t('common.point_other')),
          isSortable: false,
          value: 'points',
          align: 'start',
        },
        {
          text: _.capitalize(this.$t('common.action_other')),
          isSortable: false,
          value: 'actions',
          align: 'center',
        },
      ],
      isLoading: true,
      totalRecordsInData: 0,
      currentPage: 1,
      DEFAULT_SIZE: 20,
      revs: [],
      searchTerms: '',
      isDeleting: false,
    }
  },

  async mounted() {
    const organization = await this.getOrganization()
    this.breadcrumbs = [
      {
        href: '/admin/organizations',
        name: this.$t('common.organization_other'),
      },
      {
        href: `/organization-settings/${this.organizationId}`,
        name: organization.name,
      },
      { name: this.$t('common.rev_other') },
    ]
  },

  computed: {
    ...mapState({
      organization: state => state.organization.organization,
    }),

    items() {
      return this.revs.map(rev => {
        return {
          name: rev.name,
          owner: this.getFullName(rev.owner),
          coach: this.getFullName(rev.coach),
          state: rev.currentPhase,
          condition:
            this.$t(`common.rev_condition.${_.snakeCase(rev.status)}`) ||
            rev.revCondition,
          points: rev.points,
          id: utils.getEntityId(rev),
          typeText:
            this.organization?.customNamings?.[rev.entityType]?.singular ||
            this.$t(`common.rev_type.${_.snakeCase(rev.entityType)}_one`),
          type: rev.entityType,
        }
      })
    },

    options: {
      get() {
        return {
          itemsLength: this.totalRecordsInData,
          itemsPerPage: this.DEFAULT_SIZE,
          multiSort: false,
          mustSort: true,
          page: 1,
        }
      },
      set(event) {
        this.getOrganizationRevs({
          page: event.page,
          itemsPerPage: event.itemsPerPage,
          sortDesc: event.sortDesc[0],
          sortBy: event.sortBy[0],
        })
      },
    },

    totalPages() {
      return Math.ceil(this.totalRecordsInData / this.DEFAULT_SIZE)
    },
  },

  methods: {
    ...mapActions({
      getRevs: 'getOrganizationRevs',
      deleteImprovementIdea: 'improvementIdea/deleteRev',
      deleteQuickAction: 'quickAction/delete',
      deleteProblemSolving: 'problemSolving/delete',
    }),

    async getOrganizationRevs(options) {
      this.isLoading = true
      const size = options.itemsPerPage || this.DEFAULT_SIZE
      const payload = {
        organizationId: this.organizationId,
        limit: size,
        from: options.page * size - size,
        terms: this.searchTerms ?? null,
      }
      try {
        const response = await this.getRevs(payload)
        this.revs = response?.results ?? []
        this.totalRecordsInData = response?.totalRecordsInData ?? 0
      } catch (err) {
        this.showError(
          this.$t(
            'error.500.something_bad_happened_we_probably_did_something_wrong'
          )
        )
      } finally {
        this.isLoading = false
      }
    },

    handleSearch(terms) {
      this.searchTerms = terms
      this.getOrganizationRevs(this.options)
    },

    getRoute(type, revId) {
      if (type === 'improvementIdea')
        return `/rev/${this.organizationId}/${revId}`
      return `/${_.kebabCase(type)}/${this.organizationId}/${revId}`
    },

    getOrganization() {
      return this.$store.dispatch('getOrganization', {
        organizationId: this.organizationId,
      })
    },

    closeModal() {
      this.showDeleteModal = false
      this.rev = {}
    },

    openRevRemovalConfirmationModal(rev) {
      this.showDeleteModal = true
      this.rev = rev
    },

    removeRevByType() {
      switch (this.rev.type) {
        case 'quickAction':
          this.deleteRev(this.deleteQuickAction)
          break
        case 'improvementIdea':
          this.deleteRev(this.deleteImprovementIdea)
          break
        case 'problemSolving':
          this.deleteRev(this.deleteProblemSolving)
          break
        default:
          break
      }
    },

    async deleteRev(deleteAction) {
      try {
        this.isDeleting = true
        deleteAction({
          organizationId: this.organizationId,
          revId: this.rev.id,
        })
        this.showSuccess(this.$t('rev.rev_deleted_successfully'))
      } catch (err) {
        this.showError(
          this.$t(
            'error.500.something_bad_happened_we_probably_did_something_wrong'
          )
        )
      } finally {
        this.isDeleting = false
        this.closeModal()
        this.getOrganizationRevs({
          page: 1,
          itemsPerPage: this.DEFAULT_SIZE,
        })
      }
    },

    triggerRevViewEvent(rev) {
      this.trackView(rev)
    },
  },
}
</script>
